import { Video, MapPin, GraduationCap } from 'lucide-react';

// Event kinds share one visual language across the calendar, cards and pages.
export const EVENT_KINDS = {
  in_person: { label: 'In-person event', short: 'In person', Icon: MapPin, chip: 'bg-primary text-white', tile: 'bg-primary', dot: 'bg-primary', date: 'from-teal to-teal-dark', pill: 'bg-teal/15 text-teal-dark dark:text-teal-light' },
  online: { label: 'Online event', short: 'Online', Icon: Video, chip: 'bg-secondary text-white', tile: 'bg-secondary', dot: 'bg-secondary', date: 'from-lavender to-lavender-dark', pill: 'bg-lavender/25 text-lavender-dark dark:text-lavender-light' },
  course_launch: { label: 'Course launch', short: 'Course launch', Icon: GraduationCap, chip: 'bg-gradient-to-r from-primary to-secondary text-white', tile: 'bg-gradient-to-br from-primary to-secondary', dot: 'bg-gradient-to-br from-primary to-secondary', date: 'from-teal to-lavender-dark', pill: 'bg-gradient-to-r from-teal/25 to-lavender/35 text-text-main' },
};

export const kindOf = (ev) => EVENT_KINDS[ev.event_type] || EVENT_KINDS.in_person;

export const formatPrice = (cents) => {
  if (!cents) return 'Free';
  const dollars = cents / 100;
  return `$${Number.isInteger(dollars) ? dollars : dollars.toFixed(2)}`;
};

const tzOf = (ev) => ev.timezone || 'America/New_York';

// A malformed date must never blank a whole page: format to '' instead of throwing.
const safe = (fn) => (...args) => { try { return fn(...args); } catch { return ''; } };

// YYYY-MM-DD of the event in its own timezone, so it lands on the right calendar day.
export const eventDayKey = safe((ev) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: tzOf(ev), year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date(ev.starts_at)));

export const formatEventDate = safe((ev, opts = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) =>
  new Intl.DateTimeFormat('en-US', { timeZone: tzOf(ev), ...opts }).format(new Date(ev.starts_at)));

export const formatEventTime = safe((ev) => {
  const fmt = (iso, withZone) => new Intl.DateTimeFormat('en-US', {
    timeZone: tzOf(ev), hour: 'numeric', minute: '2-digit', ...(withZone ? { timeZoneName: 'short' } : {}),
  }).format(new Date(iso));
  return ev.ends_at ? `${fmt(ev.starts_at)} to ${fmt(ev.ends_at, true)}` : fmt(ev.starts_at, true);
});

export const formatDateTimeShort = safe((iso, tz) =>
  new Intl.DateTimeFormat('en-US', { timeZone: tz || 'America/New_York', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(iso)));

// The refund deadline shown to a registrant: refund_window_hours before the event starts.
export const refundDeadline = (ev) =>
  new Date(new Date(ev.starts_at).getTime() - (ev.refund_window_hours || 0) * 3600 * 1000);

// Remembered across signup/onboarding so a visitor lands back on the event they came for.
const RETURN_KEY = 'sirati_return_to';
export const saveReturnTo = (path) => { try { localStorage.setItem(RETURN_KEY, path); } catch { /* private mode */ } };
export const takeReturnTo = () => {
  try {
    const path = localStorage.getItem(RETURN_KEY);
    if (!path) return null;
    localStorage.removeItem(RETURN_KEY);
    return /^\/events\/[0-9a-f-]{36}$/i.test(path) ? path : null;
  } catch { return null; }
};

const icsStamp = (iso) => new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
const eventEnd = (ev) => ev.ends_at || new Date(new Date(ev.starts_at).getTime() + 2 * 3600 * 1000).toISOString();
const eventWhere = (ev) => [ev.venue_name, ev.venue_address].filter(Boolean).join(', ') || ev.online_url || ev.city || '';
const eventLink = (ev) => `${window.location.origin}/events/${ev.id}`;

// "Add to Google Calendar": opens Google's own pre-filled "new event" screen (nothing is sent to us or stored).
// The location is only included once it is revealed to this member.
export const googleCalendarUrl = (ev) => {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: ev.title,
    dates: `${icsStamp(ev.starts_at)}/${icsStamp(eventEnd(ev))}`,
    details: [ev.description, eventLink(ev)].filter(Boolean).join('\n\n'),
    location: eventWhere(ev),
    ctz: ev.timezone || 'America/New_York',
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

// "Add to my calendar" (Apple, Outlook and others): a minimal .ics file.
export const downloadIcs = (ev) => {
  const stamp = icsStamp;
  const esc = (t) => String(t || '').replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
  const end = eventEnd(ev);
  const where = eventWhere(ev);
  const lines = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Sirati//Events//EN', 'BEGIN:VEVENT',
    `UID:${ev.id}@sirati.co`, `DTSTAMP:${stamp(new Date().toISOString())}`,
    `DTSTART:${stamp(ev.starts_at)}`, `DTEND:${stamp(end)}`,
    `SUMMARY:${esc(ev.title)}`, `DESCRIPTION:${esc([ev.description, eventLink(ev)].filter(Boolean).join('\n\n'))}`, `LOCATION:${esc(where)}`, `URL:${eventLink(ev)}`,
    'END:VEVENT', 'END:VCALENDAR',
  ];
  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${ev.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'event'}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
